<template>
  <div class="content">
    <div class="container-fluid">
      <div class="card">
        <div class="card-header">
          <h4 class="card-title"><i class="icon-svg icon-clock"></i>{{$t('solicitudes_pendientes')}}</h4>
        </div>

        <div class="card-body table-responsive">
          <table id="pendingRequestsTable" class="table table-striped table-hover w-100 table_">
            <thead>
            <tr>
              <th>Carpeta</th>
              <th>Subcarpeta</th>
              <th>Solicitud</th>
              <th>Estado</th>
              <th>Fecha</th>
              <th>Documento vinculado</th>
            </tr>
            </thead>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import Vue from "vue";
import moment from "moment";
import BaseSelect from "@/components/Inputs/BaseSelect.vue";
import { dataTableLengthMenu, dataTableEsLanguage, enhanceDataTableLength } from "../../mixins";

// Fila de filtros junto al buscador de la tabla: rango de fechas (una sola
// caja, inicio y fin a la vez) + desplegable de estado. Se define como
// componente aparte porque va montado a mano dentro del contenedor que
// DataTables genera para el buscador (".dataTables_filter"), que queda
// fuera del árbol de render de este componente — no se puede colocar
// directamente en el <template> de arriba.
const PendingRequestsFilters = Vue.extend({
  components: { BaseSelect },
  props: {
    onChangeFecha: { type: Function, required: true },
    onChangeEstado: { type: Function, required: true },
  },
  data() {
    return {
      // Sin opción "Todos": el aspa de borrar del propio combobox (siempre
      // visible, ver .e-clear-icon en variations.scss) ya cumple esa
      // función — quitar la selección equivale a no filtrar por estado.
      estado: "",
      estadoOptions: [
        { id: "VINCULADO", name: this.$t("Vinculados") },
        { id: "NO_VINCULADO", name: this.$t("Sin vincular") },
      ],
    };
  },
  watch: {
    estado(value) {
      this.onChangeEstado(value);
    },
  },
  methods: {
    onDateRangeChange(args) {
      this.onChangeFecha(args?.startDate || null, args?.endDate || null);
    },
  },
  // La app usa el build "runtime-only" de Vue (sin compilador de plantillas
  // en el cliente), así que un "template" en forma de string no se puede
  // procesar aquí: hace falta una función render.
  render(h) {
    return h("div", { class: "cd-table-extra-filters" }, [
      h("ejs-daterangepicker", {
        class: "cd-filter-daterange",
        props: {
          placeholder: this.$t("filter_fecha"),
          format: "dd/MM/yyyy",
          locale: "es",
          showClearButton: true,
        },
        on: { change: this.onDateRangeChange },
      }),
      h(BaseSelect, {
        class: "cd-filter-estado-select",
        props: {
          items: this.estadoOptions,
          value: this.estado,
          placeholder: this.$t("filter_estado"),
        },
        on: {
          select: (value) => {
            this.estado = value;
          },
        },
      }),
    ]);
  },
});

export default {
  mounted() {
    this.initDataTable();
  },

  beforeDestroy() {
    // $.fn.dataTable.ext.search es un array GLOBAL, compartido por todas las
    // tablas de la app: si no se quita aquí la función que se registra en
    // addCustomFilters(), cada vez que se vuelve a esta vista se acumula una
    // copia más y además seguiría filtrando (con datos ya obsoletos) el
    // resto de listados de la aplicación.
    if (this._pendingRequestsSearchFn && window.$ && window.$.fn.dataTable) {
      const ext = window.$.fn.dataTable.ext.search;
      const idx = ext.indexOf(this._pendingRequestsSearchFn);
      if (idx > -1) ext.splice(idx, 1);
    }
    if (this._filtersInstance) {
      this._filtersInstance.$destroy();
      if (this._filtersInstance.$el?.parentNode) {
        this._filtersInstance.$el.parentNode.removeChild(this._filtersInstance.$el);
      }
      this._filtersInstance = null;
    }
    if (window.$) window.$("#pendingRequestsTable").off("click.cdFolderLink");
  },

  methods: {
    buildFolderPath(folderId, folderMap) {
      const parts = [];
      let current = folderMap[folderId];
      let guard = 0;

      while (current && guard < 50) {
        parts.unshift(current.name);
        current = folderMap[current.parent_id];
        guard++;
      }

      return parts.join(" / ");
    },
    initDataTable() {
      const vm = this;

      window.$(".table_").DataTable({
        // El "Processing..." nativo de DataTables se superponía con el
        // texto de "sin resultados". Se usa en su lugar el mismo overlay
        // (puntos verdes) que el resto de vistas, vía el store global.
        processing: false,
        serverSide: false,
        pageLength: 10,
        lengthMenu: dataTableLengthMenu,
        destroy: true,
        orderCellsTop: true,
        autoWidth: false,
        dom: "lBfrtip",

        ajax: async function (data, callback) {
          vm.$store.dispatch("isLoadingOn");
          try {
            // 1️⃣ Solicitudes
            const res = await vm.$axios.get("/request_documentation_all");
            const documents = res.data.documents || [];
            const requestDocs = res.data.request_doc || [];

            // 2️⃣ Todos los documentos (carpetas + archivos)
            const allDocsRes = await vm.$axios.get("/documents");
            const allDocs = allDocsRes.data || [];

            // 3️⃣ Mapas rápidos
            const requestMap = {};
            requestDocs.forEach(r => {
              requestMap[r.request_id] = r;
            });

            const documentsMap = {};
            allDocs.forEach(d => {
              documentsMap[d.id] = d;
            });

            // 4️⃣ Construcción final de filas
            const rows = documents.map(doc => {
              const linked = requestMap[doc.id];
              const folder = documentsMap[doc.folder_id];

              let carpeta = "INICIO";
              let subcarpeta = "";
              let doc_name = "";

              if (folder) {
                subcarpeta = folder.name;

                const fullPath = vm.buildFolderPath(folder.id, documentsMap);
                const parentPath = fullPath
                    .replace(folder.name, "")
                    .replace(/\/$/, "")
                    .trim();

                carpeta = parentPath
                    ? `INICIO / ${parentPath}`
                    : "INICIO";
              }

              if (linked && documentsMap[linked.document_id]) {
                doc_name = documentsMap[linked.document_id].name;
              }

              return {
                carpeta,
                subcarpeta,
                // Folder the request is linked to, used by the "Subcarpeta"
                // link to open it in the documents view. Null when the
                // request hangs off the root.
                folder_id: folder ? folder.id : null,
                solicitud: doc.name,
                estado: linked ? "VINCULADO" : "",
                // Se guarda el valor sin formatear (para que la columna
                // ordene cronológicamente) y se formatea solo al mostrarlo,
                // vía el "render" de la columna más abajo.
                created_at: linked ? linked.created_at : doc.created_at,
                doc_name
              };
            });

            callback({ data: rows });
            vm.$store.dispatch("isLoadingOff");

          } catch (err) {
            console.error("❌ Error cargando DataTable", err);
            callback({ data: [] });
            vm.$store.dispatch("isLoadingOff");
          }
        },

        columns: [
          { data: "carpeta" },
          {
            data: "subcarpeta",
            // Only "display" gets the link; sorting, searching and the
            // Excel/PDF/print export keep the plain folder name. The name
            // is escaped because the cell is painted as HTML. The real href
            // keeps middle click and "open in new tab" working; a plain
            // click is routed without a reload, see bindFolderLinks.
            render: function (data, type, row) {
              if (type !== "display" || !row.folder_id) return data;

              const name = window.$("<div>").text(data || "").html();
              return `<a href="/admin/documents?path=${row.folder_id}" class="cd-folder-link" data-folder-id="${row.folder_id}">${name}</a>`;
            }
          },
          { data: "solicitud" },
          {
            data: "estado",
            render: function (data, type) {
              if (type === "display" && data === "VINCULADO") {
                return `
                  <span class="badge badge-linked">
                    <i class="icon-svg icon-circle-check"></i>Vinculado
                  </span>
                `;
              }
              return data;
            }
          },
          {
            data: "created_at",
            // Solo se formatea para "display" (lo que se ve); el resto de
            // usos (ordenar, filtrar, exportar) siguen trabajando con la
            // fecha en bruto, que sí ordena cronológicamente — con el
            // texto ya formateado "DD/MM/YYYY", DataTables comparaba como
            // texto plano y ordenaba por el día del mes antes que por el
            // año, dejando las fechas mezcladas en vez de al principio o
            // al final.
            render: function (data, type) {
              return type === "display" ? vm.formatDate(data) : data;
            }
          },
          { data: "doc_name" }
        ],

        buttons: [
          { extend: "excel", className: "btn", text: '<i class="icon-svg icon-file-excel"></i><span class="btn-label">Excel</span>', attr: { "data-tooltip": "Excel" } },
          { extend: "pdf", className: "btn", text: '<i class="icon-svg icon-file-pdf"></i><span class="btn-label">PDF</span>', attr: { "data-tooltip": "PDF" } },
          { extend: "print", className: "btn", text: '<i class="icon-svg icon-printer"></i><span class="btn-label">Imprimir</span>', attr: { "data-tooltip": "Imprimir" } }
        ],

        language: dataTableEsLanguage,

        initComplete: function () {
          vm.addCustomFilters(this.api());
          vm.bindFolderLinks();
          enhanceDataTableLength();
        }
      });
    },

    // One delegated listener for every "Subcarpeta" link, so it survives the
    // redraws DataTables does on paging, sorting and filtering. A plain click
    // goes through the router (no full reload); modified or middle clicks are
    // left to the browser so the link can still open in a new tab. The
    // documents view opens the folder from ?path= in openLinkedFolder.
    bindFolderLinks() {
      const vm = this;

      window.$("#pendingRequestsTable")
        .off("click.cdFolderLink")
        .on("click.cdFolderLink", "a.cd-folder-link", function (event) {
          if (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) return;

          event.preventDefault();
          const folderId = window.$(this).data("folder-id");
          const navigation = vm.$router.push({
            path: "/admin/documents",
            query: { path: String(folderId) },
          });
          // vue-router 3 reports a refused navigation as an unhandled promise.
          if (navigation && navigation.catch) navigation.catch(() => {});
        });
    },

    // Sustituye la fila de filtros por columna (redundante con el buscador
    // general, que ya busca en todas las columnas) por dos filtros junto al
    // buscador: uno de fecha (exacta o entre dos fechas) sobre la columna
    // "Fecha", y un desplegable de estado ("Todos"/"Vinculados"/"Sin
    // vincular") sobre la columna "Estado".
    addCustomFilters(api) {
      const vm = this;
      const filterContainer = window
        .$("#pendingRequestsTable")
        .closest(".dataTables_wrapper")
        .find(".dataTables_filter")
        .get(0);

      let fechaInicio = null;
      let fechaFin = null;
      let estadoFiltro = "";

      // Si esta vista se vuelve a montar, se quita tanto la instancia Vue
      // como la función de búsqueda de la montura anterior antes de crear
      // las nuevas (ver beforeDestroy).
      if (vm._filtersInstance) {
        vm._filtersInstance.$destroy();
        vm._filtersInstance.$el?.parentNode?.removeChild(vm._filtersInstance.$el);
      }
      if (vm._pendingRequestsSearchFn) {
        const ext = window.$.fn.dataTable.ext.search;
        const idx = ext.indexOf(vm._pendingRequestsSearchFn);
        if (idx > -1) ext.splice(idx, 1);
      }

      vm._filtersInstance = new PendingRequestsFilters({
        parent: vm,
        propsData: {
          onChangeFecha(start, end) {
            fechaInicio = start;
            fechaFin = end;
            api.draw();
          },
          onChangeEstado(value) {
            estadoFiltro = value;
            api.draw();
          },
        },
      }).$mount();
      window.$(filterContainer).prepend(vm._filtersInstance.$el);

      vm._pendingRequestsSearchFn = function (settings, data) {
        // $.fn.dataTable.ext.search es global: sin esto, este filtro también
        // se aplicaría (con datos que no le corresponden) al resto de tablas
        // de la aplicación.
        if (settings.nTable.id !== "pendingRequestsTable") return true;

        const estadoRow = data[3] || "";
        if (estadoFiltro === "VINCULADO" && estadoRow !== "VINCULADO") return false;
        if (estadoFiltro === "NO_VINCULADO" && estadoRow === "VINCULADO") return false;

        if (fechaInicio || fechaFin) {
          // data[4] ya no es el texto formateado (ver el render de la
          // columna "created_at" más arriba): es la fecha en bruto que
          // devuelve la API, así que se deja que moment la detecte sola en
          // vez de forzar el formato "DD/MM/YYYY HH:mm".
          const rowDate = moment(data[4]);
          if (!rowDate.isValid()) return false;
          if (fechaInicio && rowDate.isBefore(moment(fechaInicio).startOf("day"))) {
            return false;
          }
          if (fechaFin && rowDate.isAfter(moment(fechaFin).endOf("day"))) {
            return false;
          }
        }

        return true;
      };

      window.$.fn.dataTable.ext.search.push(vm._pendingRequestsSearchFn);
    },

    formatDate(value) {
      return moment(value).format("DD/MM/YYYY HH:mm");
    }
  }
};
</script>

<style>
.badge{
  font-size:11px;
}
/* Estado "Vinculado": píldora suave en el verde de marca en vez del badge
   gris con emoji anterior. */
.badge-linked {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 100px;
  background-color: rgba(46, 158, 79, 0.14);
  color: #2E9E4F;
  font-weight: 500;
  font-size: 11.5px;
  letter-spacing: .2px;
}
.badge-linked .icon-svg {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
}

/* "Subcarpeta" link to the folder in the documents view. */
.cd-folder-link {
  color: var(--cd-brand-green);
  text-decoration: none;
}
.cd-folder-link:hover,
.cd-folder-link:focus {
  color: var(--cd-brand-green-hover);
  text-decoration: underline;
}

.card .table_ thead > tr:nth-child(1) > th:nth-child(3)
{
  width: 30% !important;
}

/* Filtros extra junto al buscador: rango de fechas + estado, montados como
   una instancia Vue real (ejs-daterangepicker + base-select) dentro del
   contenedor que DataTables genera para el buscador. Limitado al wrapper de
   esta tabla (DataTables genera su id añadiendo "_wrapper" al id de la
   propia tabla) para no afectar al resto de listados de la app, que
   comparten la misma clase .dataTables_filter. */
#pendingRequestsTable_wrapper .dataTables_filter {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
/* El <label> nativo "Buscar: [input]" trae un margin-bottom:5px de
   Bootstrap (pensado para cuando el label va encima del campo, no al
   lado). Con align-items:center eso desplazaba su caja completa ~2.5px
   más arriba que la de nuestros filtros (sin ese margen), descuadrando la
   alineación vertical de los tres controles. */
#pendingRequestsTable_wrapper .dataTables_filter > label {
  margin-bottom: 0;
}
.cd-table-extra-filters {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Los componentes de Syncfusion (daterangepicker, combobox de base-select)
   ya traen su propio contorno verde global (.e-input-group.e-control-wrapper
   / .form-control en variations.scss), pero a 42px de alto para encajar en
   formularios — aquí deben verse igual de altos que el cuadro de búsqueda
   de al lado (35.6px, ver ".dataTables_wrapper .dataTables_filter input"),
   así que se ajusta solo dentro de este filtro. */
.cd-table-extra-filters .e-input-group.e-control-wrapper,
.cd-table-extra-filters .form-control {
  height: 35.6px !important;
}
.cd-table-extra-filters .e-input-group.e-control-wrapper input.e-input {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}
/* ".dataTables_filter input" (variations.scss) da a CUALQUIER <input> de
   este contenedor un ancho fijo de 250px y el icono de lupa de fondo — sin
   esto se colaba también en el input interno del daterangepicker y del
   combobox, deformándolos. El ancho real de cada control se fija más abajo
   en su contenedor (".e-date-range-wrapper" / ".cd-filter-estado-select"),
   así que aquí basta con que el input ocupe el 100% de ese contenedor.
   Ese mismo selector también le pone BORDE Y RADIO propios al input
   (pensados para un input suelto, no para uno ya envuelto en la caja de
   Syncfusion) — de ahí el "doble borde": el de la caja exterior
   (.e-input-group, el bueno) y el de este input interior, sin relación
   con el primero. Se anulan aquí los dos.
   Necesita el id del wrapper por especificidad: ".dataTables_wrapper
   .dataTables_filter input" ya tiene 2 clases + !important, y con solo
   ".cd-table-extra-filters input" (1 clase) seguía perdiendo el empate.
   Se excluyen los ".e-daterange-hidden": son dos inputs auxiliares que el
   propio daterangepicker no debería mostrar nunca (solo guardan el valor
   de inicio/fin por separado), pero su regla de tema no tiene !important y
   aquí no se estaba aplicando de forma fiable, dejándolos ocupar hueco de
   verdad en el layout — se sacan del todo con display:none. */
#pendingRequestsTable_wrapper .cd-table-extra-filters input:not(.e-daterange-hidden) {
  width: 100% !important;
  background-image: none !important;
  padding: 0 4px !important;
  font-size: 0.85rem !important;
  border: 0 !important;
  border-radius: 0 !important;
}
#pendingRequestsTable_wrapper .cd-table-extra-filters .e-daterange-hidden {
  display: none !important;
}
/* El icono de calendario del daterangepicker es un glifo de icon-font
   (fuente "e-icons"), distinto al resto de iconos tabler.io de la app. Se
   sustituye por el mismo tabler "calendar" que usa el resto de la interfaz
   (p.ej. el título de esta misma página), en el gris neutro de los demás
   iconos de estos filtros. */
.cd-table-extra-filters .e-range-icon {
  color: transparent !important;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%238a8f9c' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><rect x='4' y='5' width='16' height='16' rx='2'/><line x1='16' y1='3' x2='16' y2='7'/><line x1='8' y1='3' x2='8' y2='7'/><line x1='4' y1='11' x2='20' y2='11'/><line x1='8' y1='15' x2='8' y2='15'/><line x1='12' y1='15' x2='12' y2='15'/><line x1='16' y1='15' x2='16' y2='15'/></svg>") !important;
  background-repeat: no-repeat !important;
  background-position: center !important;
  background-size: 16px 16px !important;
}
.cd-table-extra-filters .e-range-icon::before {
  content: "" !important;
}
/* El tema pone un borde lateral a los iconos (pensado como separador
   cuando hay varios botones seguidos dentro del mismo campo, p.ej. reloj +
   calendario), que aquí se veía como un marco suelto alrededor del icono
   de calendario y de la flecha del desplegable de Estado. */
.cd-table-extra-filters .e-input-group-icon {
  border-left: 0 !important;
  border-right: 0 !important;
}

/* El daterangepicker no admite una clase propia en el elemento que genera:
   Vue solo llega a poner "cd-filter-daterange" en el <input> original, y
   Syncfusion lo envuelve luego en tiempo de ejecución con este <span>, que
   es el que de verdad hace de caja del control (icono, aspa, ancho...). */
.cd-table-extra-filters .e-date-range-wrapper {
  /* Suficiente para que quepa el rango completo ("dd/mm/aaaa - dd/mm/aaaa")
     sin que el propio input lo recorte con puntos suspensivos, más los dos
     iconos (aspa de borrar, forzada siempre visible a nivel global, y el
     de calendario) a ~34px cada uno. */
  width: 250px;
}
/* El wrapper es flex (lo fija el propio tema de Syncfusion) y, por
   defecto, el input no crece para ocupar el hueco libre — todo el ancho
   sobrante se quedaba después de los iconos en vez de repartirse, dejando
   el texto apretado contra el icono y un bloque en blanco al final. */
.cd-table-extra-filters .e-date-range-wrapper input.e-input {
  flex: 1 1 auto !important;
}
/* BaseSelect renderiza su propia raíz como ".row.form-group" (gutters
   negativos de Bootstrap incluidos) y sus columnas internas como ".col-12":
   aquí, dentro de una caja de filtro estrecha, eso desbordaba por los
   lados y añadía espacio de sobra por el "form-group" pensado para
   formularios completos. */
.cd-filter-estado-select {
  width: 175px;
  margin: 0 !important;
}
.cd-table-extra-filters .form-group {
  margin-bottom: 0 !important;
}
.cd-filter-estado-select > .col-12 {
  padding: 0 !important;
}

/* Estos dos controles van junto al buscador de la tabla (borde gris, no el
   verde de los formularios) — solo se ponen verdes en foco, igual que el
   propio buscador. El ":not(.e-input-focus)" es necesario: esta hoja de
   estilos del componente se inyecta más tarde que variations.scss (la ruta
   es de carga diferida) y, a igualdad de !important y de especificidad,
   gana la regla que se aplica después — sin la exclusión, este gris se
   comería también el verde de foco global. */
.cd-table-extra-filters .e-input-group.e-control-wrapper:not(.e-input-focus) {
  border-color: var(--cd-border-light, #e2e5ec) !important;
}
/* Los iconos (flecha del desplegable, calendario) quedaban con demasiado
   aire respecto al borde derecho de su caja. */
.cd-table-extra-filters .e-input-group.e-control-wrapper {
  padding-right: 4px !important;
}
</style>
